/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { ImgHTMLAttributes } from 'react';
import styled from '@xstyled/styled-components';
import DefaultLogo from './logo.svg';
import DefaultDarkThemeLogo from './dark-theme-logo.svg';

import FbLogoImg from '../../assets/social_icons/facebook_icon.svg';
import InLogoImg from '../../assets/social_icons/linkedin_icon.svg';
import TwitterLogoImg from '../../assets/social_icons/twitter_icon.svg';
import YoutubeLogoImg from '../../assets/social_icons/youtube_icon.svg';
import G2LogoImg from '../../assets/social_icons/g2_icons.svg';
import G2SquareLogoImg from '../../assets/social_icons/g2_square_icons.svg';

import { t } from 'ttag';
import useIsDarkMode from '~/lib/themes/hooks/useIsDarkMode';
import { SITE_NAME } from '~/lib/a11y/formatDocumentTitle';

const Image = styled.img<{
  title?: string;
  height?: number | string | undefined;
  width?: number | string | undefined;
}>`
  max-height: ${p => p.height}px;
  max-width: ${p => (p.width ? p.width + 'px' : '')};
  display: table;
  margin: auto;
`;

export const Logo = (props: ImgHTMLAttributes<HTMLImageElement>) => {
  if (props.src) {
    const { alt = '', ...rest } = props;
    return <Image {...rest} alt={alt} />;
  }
  // Default SVG mark: accessible name via aria-label (not HTML title).
  const label = props.alt?.trim() || props.title?.trim() || SITE_NAME;
  return (
    <Image
      as={DefaultLogo}
      height={props.height}
      role="img"
      aria-label={label}
    />
  );
};

export const DarkThemeLogo = (props: ImgHTMLAttributes<HTMLImageElement>) => {
  return (
    <Image
      as={DefaultDarkThemeLogo}
      height={props.height}
      title={props?.title || t`Logo`}
    />
  );
};

type SocialLogoProps = {
  'aria-hidden'?: boolean | 'true' | 'false';
};

export const FBLogo = ({ 'aria-hidden': ariaHidden }: SocialLogoProps) => {
  return (
    <Image
      as={FbLogoImg}
      height="32px"
      width="32px"
      aria-hidden={ariaHidden}
    />
  );
};

export const INLogo = ({ 'aria-hidden': ariaHidden }: SocialLogoProps) => {
  return (
    <Image
      as={InLogoImg}
      height="32px"
      width="32px"
      aria-hidden={ariaHidden}
    />
  );
};

export const YoutubeLogo = (_props: ImgHTMLAttributes<HTMLImageElement>) => {
  return <Image as={YoutubeLogoImg} height="46px" width="46px" />;
};

export const TwitterLogo = ({ 'aria-hidden': ariaHidden }: SocialLogoProps) => {
  return (
    <Image
      as={TwitterLogoImg}
      height="32px"
      width="32px"
      style={{ backgroundColor: '#FFF' }}
      aria-hidden={ariaHidden}
    />
  );
};

export const G2Logo = (_props: ImgHTMLAttributes<HTMLImageElement>) => {
  return <Image as={G2LogoImg} height="32px" width="32px" />;
};

export const G2SquareLogo = ({
  'aria-hidden': ariaHidden
}: SocialLogoProps) => {
  return (
    <Image
      as={G2SquareLogoImg}
      height="32px"
      width="32px"
      aria-hidden={ariaHidden}
    />
  );
};

export const ThemeAwareLogo = (props: ImgHTMLAttributes<HTMLImageElement>) => {
  const { height, title } = props;
  const isDarkMode = useIsDarkMode();

  return isDarkMode ? (
    <DarkThemeLogo height={height} title={title} />
  ) : (
    <Logo height={height} title={title} />
  );
};
